// The renderer's read of the launch machine (sdk ADR-016).
import { getPlatformRef } from '@localagent/platform';
import {
  emitLifecycleEvent,
  launchRefusal,
  type LaunchRefusalCode,
  type LaunchState,
  type LaunchStateName,
} from '@localagent/sdk';

interface LaunchReport {
  state: LaunchState;
  inFlight?: boolean;
}

interface ElectronInvoker {
  invoke: (channel: string) => Promise<unknown>;
}

const NO_BRIDGE =
  'window.electronAPI is undefined — the Electron preload bridge did not load, so the ' +
  'launch machine cannot be reached';

function optionalInvoker(): ElectronInvoker | null {
  const api = (window as unknown as { electronAPI?: Partial<ElectronInvoker> }).electronAPI;
  return api && typeof api.invoke === 'function' ? (api as ElectronInvoker) : null;
}

function invoker(): ElectronInvoker {
  const api = optionalInvoker();
  if (api === null) throw new Error(NO_BRIDGE);
  return api;
}

function refusalOf(report: unknown): LaunchRefusalCode | null {
  const state = (report as LaunchReport | null)?.state;
  if (state === undefined) {
    throw new Error('the launch report carries no state — main did not answer the launch channel');
  }
  return launchRefusal(state);
}

/** Whether a boot refusal is really a convergence failure wearing a boot cause. */
export async function readLaunchRefusal(): Promise<LaunchRefusalCode | null> {
  if (getPlatformRef().type !== 'desktop') return null;
  const api = optionalInvoker();
  if (api === null) {
    // A desktop host with no bridge loses the launch machine's own name (sdk ADR-027).
    emitLifecycleEvent('lifecycle.launch_unreadable', { cause: NO_BRIDGE });
    return null;
  }
  return refusalOf(await api.invoke('launch:report'));
}

/** The Retry behind a named convergence failure re-runs convergence. */
export async function reconverge(): Promise<LaunchRefusalCode | null> {
  return refusalOf(await invoker().invoke('launch:converge'));
}

/** Convergence can replace the daemon under a resolved address; boot only once it settles (sdk ADR-016). */
export async function awaitLaunchSettled(): Promise<LaunchRefusalCode | null> {
  if (getPlatformRef().type !== 'desktop') return null;
  const api = optionalInvoker();
  if (api === null) {
    emitLifecycleEvent('lifecycle.launch_unreadable', { cause: NO_BRIDGE });
    return null;
  }
  return refusalOf(await api.invoke('launch:settled'));
}

export async function readLaunchPhase(): Promise<LaunchStateName | null> {
  if (getPlatformRef().type !== 'desktop') return null;
  const api = optionalInvoker();
  if (api === null) return null;
  const report = (await api.invoke('launch:report')) as LaunchReport | null;
  if (report?.state === undefined) {
    throw new Error('the launch report carries no state — main did not answer the launch channel');
  }
  return report.state.state;
}
